import { Box, Typography, styled, useMediaQuery, useTheme, Collapse } from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import arrowDownIcon from "/assets/arrow-down.svg";

const FooterContainer = styled(Box)(({ theme }) => ({
  width: "100%",
  padding: theme.spacing(4, 2),
  backgroundColor: "var(--background-white)",
  [theme.breakpoints.up("md")]: {
    padding: theme.spacing(6, 4),
  },
}));

const FooterContent = styled(Box)(({ theme }) => ({
  maxWidth: "1200px",
  margin: "0 auto",
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(4),
}));

const FooterRow = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(4),
  [theme.breakpoints.up("md")]: {
    flexDirection: "row",
    gap: "58px",
  },
}));

const FooterColumn = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2.5),
  flex: 1,
  "&:last-of-type": {
    gap: theme.spacing(1.5),
  },
}));

const MobileFooterColumn = styled(FooterColumn)(({ theme }) => ({
  gap: theme.spacing(2),
}));

const FooterTitle = styled(Typography)({
  fontSize: 18,
  fontWeight: 600,
  lineHeight: "22px",
  color: "var(--text-primary)",
  fontFamily: "var(--font-family)",
});

const FooterList = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2),
}));

const FooterLink = styled(Typography)({
  fontSize: 14,
  fontWeight: 400,
  lineHeight: "17px",
  color: "var(--text-secondary)",
  fontFamily: "var(--font-family)",
  cursor: "pointer",
  transition: "color 0.2s ease",
  "&:hover": {
    color: "var(--text-primary)",
    textDecoration: "underline",

  },
});



const MobileAccordionHeader = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  cursor: "pointer",
  userSelect: "none",
  width: "100%",
}));

const ArrowIcon = styled(Box)<{ open: boolean }>(({ open }) => ({
  width: 24,
  height: 24,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  transition: "transform 0.2s ease",
  transform: open ? "rotate(180deg)" : "rotate(0deg)",
  "& img": {
    width: "100%",
    height: "100%",
    display: "block",
  },
}));

interface AccordionSectionProps {
  title: string;
  links?: string[];
  defaultOpen?: boolean;
  isMobile: boolean;
}

const AccordionSection = ({ title, links, defaultOpen = false, isMobile }: AccordionSectionProps) => {
  const [open, setOpen] = useState(defaultOpen);

  if (!isMobile) {
    return (
      <FooterColumn>
        <FooterTitle>{title}</FooterTitle>
        {links && (
          <FooterList>
            {links.map((link, index) => (
              <FooterLink key={index}>{link}</FooterLink>
            ))}
          </FooterList>
        )}
      </FooterColumn>
    );
  }

  return (
    <MobileFooterColumn>
      <MobileAccordionHeader onClick={() => setOpen(!open)}>
        <FooterTitle>{title}</FooterTitle>
        <ArrowIcon open={open}>
          <img src={arrowDownIcon} alt="" />
        </ArrowIcon>
      </MobileAccordionHeader>
      {links && (
        <Collapse in={open}>
          <FooterList>
            {links.map((link, index) => (
              <FooterLink key={index}>{link}</FooterLink>
            ))}
          </FooterList>
        </Collapse>
      )}
    </MobileFooterColumn>
  );
};

export const Footer = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { t } = useTranslation();

  const resourcesLinks = [
    t("footer.aboutSelfmadeId"),
    t("footer.helpCenter"),
    t("footer.faq"),
    t("footer.contacts"),
    t("footer.blog"),
  ];

  const employeesLinks = [t("footer.softSkillsAssessment"), t("footer.hardSkillsAssessment")];

  const recruitersLinks = [t("footer.candidateAssessment"), t("footer.employeeDevelopment")];

  const businessLinks = [t("footer.teamAnalytics"), t("footer.hrDashboard")];

  return (
    <FooterContainer component="footer">
      <FooterContent>
        <FooterRow>
          <AccordionSection
            title={t("footer.resources")}
            links={resourcesLinks}
            defaultOpen={true}
            isMobile={isMobile}
          />
          <AccordionSection
            title={t("footer.forEmployees")}
            links={employeesLinks}
            isMobile={isMobile}
          />
          <AccordionSection
            title={t("footer.forRecruiters")}
            links={recruitersLinks}
            isMobile={isMobile}
          />
          <AccordionSection
            title={t("footer.forBusiness")}
            links={businessLinks}
            isMobile={isMobile}
          />
        </FooterRow>

        {/* <AddressText>
          Since 2018 Phuket
          <br />
          63/202 Moo 2, Kohkaew, Phuket, Muang, {isMobile && <br />}83000
          Thailand
        </AddressText> */}
      </FooterContent>
    </FooterContainer>
  );
};